import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PlayerHand from './PlayerHand';
import OpponentCard from './OpponentCard';
import CenterPiles from './CenterPiles';
import PlayerTurnIndicator from '../ui/PlayerTurnIndicator';
import ScoreBoard from '../ui/ScoreBoard';
import Card from '../cards/Card';
import { useGameState } from '../../hooks/useGameState';
import { useGameRound } from '../../hooks/useGameRound';
import { usePlayer } from '../../context/PlayerContext';
import { useGame } from '../../context/GameContext';
import { PHASES, TURN_PHASES } from '../../utils/gameRules';
import { playTrumpet, playChirp, isMuted, toggleMute, markInteracted } from '../../utils/sounds';
import { getRevealMessage, getOpponentDrawMessage, getFriendlyError } from '../../utils/flavorText';
import { useBotPlayer } from '../../hooks/useBotPlayer';
import HowToPlay from '../ui/HowToPlay';

function GameBoard() {
  const {
    roomCode,
    gameState,
    players,
    playerOrder,
    currentTurn,
    discardPile,
    deckRemaining,
    myHand,
    isMyTurn,
    roundState,
  } = useGameState();

  const { playerId, drawnCard, drawnFrom, resetPlayer } = usePlayer();
  const { dispatch } = useGame();
  const { revealInitialCards, performDraw, performSwap, performDiscard } = useGameRound();
  const [revealedPositions, setRevealedPositions] = useState([]);
  const [hasRevealed, setHasRevealed] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [error, setError] = useState(null);
  const [animating, setAnimating] = useState(null);
  const [soundMuted, setSoundMuted] = useState(isMuted());

  // Mark user interaction on first touch/click
  useEffect(() => {
    const handler = () => { markInteracted(); document.removeEventListener('click', handler); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  useBotPlayer();

  const opponents = playerOrder.filter((pid) => pid !== playerId);
  const dealerId = roundState?.dealerId;
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [lastDiscard, setLastDiscard] = useState(null);
  const prevDiscardRef = useRef(null);
  const lastDiscardTimer = useRef(null);

  // Figure out who discarded by looking at the turn number in playerOrder.
  // When a swap/discard happens, the turn advances. The player who just went
  // is the one BEFORE the current turn player in the order.
  const discardKey = useMemo(
    () => discardPile.topCard ? `${discardPile.topCard.rank}-${discardPile.topCard.suit}-${discardPile.count}` : null,
    [discardPile.topCard, discardPile.count]
  );

  useEffect(() => {
    if (!discardKey || discardKey === prevDiscardRef.current) return;
    const isFirst = prevDiscardRef.current === null;
    prevDiscardRef.current = discardKey;
    if (isFirst) return;

    // Determine who just discarded:
    // If currentTurn is WAITING_DECISION, the current player drew and the discard
    // came from someone drawing from discard (the discard pile changed because they took from it).
    // If currentTurn is WAITING_DRAW, the previous player just finished their turn.
    let whoId = null;
    if (currentTurn) {
      if (currentTurn.phase === 'WAITING_DRAW') {
        // Turn already advanced — the previous player in order just went
        const currentIdx = playerOrder.indexOf(currentTurn.playerId);
        const prevIdx = (currentIdx - 1 + playerOrder.length) % playerOrder.length;
        whoId = playerOrder[prevIdx];
      } else {
        // Still on same player's turn (WAITING_DECISION) — they drew from discard
        whoId = currentTurn.playerId;
      }
    }

    const whoDiscarded = whoId ? players[whoId]?.displayName : null;

    setLastDiscard({
      card: discardPile.topCard,
      name: whoDiscarded,
    });

    clearTimeout(lastDiscardTimer.current);
    lastDiscardTimer.current = setTimeout(() => setLastDiscard(null), 3000);
  }, [discardKey, currentTurn, players, playerOrder, discardPile.topCard]);

  // Sound: chirp when it becomes my turn
  const prevIsMyTurn = useRef(false);
  useEffect(() => {
    if (isMyTurn && !prevIsMyTurn.current) {
      playChirp();
      if (navigator.vibrate) navigator.vibrate(50);
    }
    prevIsMyTurn.current = isMyTurn;
  }, [isMyTurn]);

  // Sound + haptic: trumpet when any player goes out
  const prevAllFaceUp = useRef(false);
  useEffect(() => {
    const allFaceUp = roundState?.allFaceUp || false;
    if (allFaceUp && !prevAllFaceUp.current) {
      playTrumpet();
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    }
    prevAllFaceUp.current = allFaceUp;
  }, [roundState?.allFaceUp]);

  // Find if an opponent drew from discard (to show their drawn card)
  const activeOpponentDraw = (() => {
    if (!currentTurn || currentTurn.playerId === playerId) return null;
    if (currentTurn.phase !== TURN_PHASES.WAITING_DECISION) return null;
    const opponent = players[currentTurn.playerId];
    if (!opponent?.turnState?.hasTaken) return null;
    if (opponent.turnState.drawnFrom !== 'discard') return null;
    return {
      name: opponent.displayName,
      card: opponent.turnState.drawnCard,
    };
  })();

  const handleLeave = useCallback(() => {
    resetPlayer();
    dispatch({ type: 'RESET' });
  }, [resetPlayer, dispatch]);

  const isRevealing = gameState.phase === PHASES.REVEALING;
  const canDrawCard = isMyTurn && currentTurn?.phase === TURN_PHASES.WAITING_DRAW;
  const canDecide = isMyTurn && currentTurn?.phase === TURN_PHASES.WAITING_DECISION;

  const handleRevealCard = useCallback(
    async (position) => {
      if (!isRevealing || hasRevealed) return;
      if (revealedPositions.includes(position)) return;

      const next = [...revealedPositions, position];
      setRevealedPositions(next);

      if (next.length === 2) {
        setHasRevealed(true);
        try {
          await revealInitialCards(next);
        } catch (err) {
          setError(err.message);
          setHasRevealed(false);
        }
        setRevealedPositions([]);
      }
    },
    [isRevealing, hasRevealed, revealedPositions, revealInitialCards]
  );

  const handleDrawStock = useCallback(async () => {
    try {
      setError(null);
      await performDraw('stock');
    } catch (err) {
      setError(err.message);
    }
  }, [performDraw]);

  const handleDrawDiscard = useCallback(async () => {
    try {
      setError(null);
      await performDraw('discard');
    } catch (err) {
      setError(err.message);
    }
  }, [performDraw]);

  const handleSwap = useCallback(
    async (position) => {
      if (!canDecide || animating) return;
      try {
        setError(null);
        setAnimating('swap');
        await new Promise((r) => setTimeout(r, 300));
        await performSwap(position);
      } catch (err) {
        setError(err.message);
      } finally {
        setAnimating(null);
      }
    },
    [canDecide, animating, performSwap]
  );

  const handleDiscard = useCallback(async () => {
    if (!canDecide || animating) return;
    try {
      setError(null);
      setAnimating('discard');
      await new Promise((r) => setTimeout(r, 300));
      await performDiscard();
    } catch (err) {
      setError(err.message);
    } finally {
      setAnimating(null);
    }
  }, [canDecide, animating, performDiscard]);

  const handleCardClick = useCallback(
    (position) => {
      if (isRevealing) {
        handleRevealCard(position);
      } else if (canDecide) {
        handleSwap(position);
      }
    },
    [isRevealing, canDecide, handleRevealCard, handleSwap]
  );

  return (
    <div className="game-board">
      <div className="game-board__header">
        <button className="game-board__exit" onClick={() => setShowLeaveConfirm(true)}>
          Exit
        </button>
        <span className="game-board__round-info">
          {dealerId === playerId ? '★ ' : ''}Round {gameState.currentRound}/{gameState.totalRounds}
          <span className="game-board__room-code" onClick={() => { navigator.clipboard.writeText(roomCode).catch(() => {}); }}>{roomCode}</span>
        </span>
        <div className="game-board__header-right">
          <button
            className={`game-board__header-btn ${!soundMuted ? 'game-board__header-btn--active' : ''}`}
            onClick={() => { const m = toggleMute(); setSoundMuted(m); }}
          >
            {soundMuted ? 'Sound Off' : 'Sound On'}
          </button>
          <button className="game-board__header-btn" onClick={() => setShowHelp(true)}>Help</button>
          <button
            className="game-board__scores-toggle"
            onClick={() => setShowScores(!showScores)}
          >
            Scores {showScores ? '▴' : '▾'}
          </button>
        </div>
      </div>

      {showLeaveConfirm && (
        <div className="game-board__confirm">
          <span>Are you sure you want to leave?</span>
          <p className="game-board__confirm-sub">You won't be able to rejoin this game.</p>
          <div className="game-board__confirm-btns">
            <button className="btn btn--primary btn--sm" onClick={handleLeave}>Leave Game</button>
            <button className="btn btn--ghost btn--sm" onClick={() => setShowLeaveConfirm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {showHelp && <HowToPlay onClose={() => setShowHelp(false)} />}

      {showScores && <ScoreBoard />}

      <div className="game-board__middle">
        <div className="game-board__opponents">
          {opponents.map((pid) => (
            <OpponentCard key={pid} player={players[pid]} isDealer={pid === dealerId} />
          ))}
        </div>

        {(() => {
          const spectators = playerOrder
            .filter((pid) => players[pid]?.spectating && pid !== playerId)
            .map((pid) => players[pid]?.displayName);
          if (spectators.length === 0) return null;
          return (
            <div className="game-board__spectator-notice">
              {spectators.join(', ')} watching — joins next round
            </div>
          );
        })()}

        <CenterPiles
          discardPile={discardPile}
          deckRemaining={deckRemaining}
          onDrawStock={handleDrawStock}
          onDrawDiscard={handleDrawDiscard}
          onDiscard={handleDiscard}
          canDraw={canDrawCard}
          canDiscard={canDecide}
        />

        {lastDiscard && (
          <div className="game-board__last-discard">
            <Card card={lastDiscard.card} faceUp size="small" disabled />
            <span>{lastDiscard.name ? `${lastDiscard.name} discarded` : 'Discarded'}</span>
          </div>
        )}

        {isRevealing && (
          <div className="game-board__message">
            {getRevealMessage(2 - revealedPositions.length)}
          </div>
        )}

        <PlayerTurnIndicator />

        {activeOpponentDraw && (
          <div className="game-board__drawn game-board__drawn--opponent">
            <span>{getOpponentDrawMessage(activeOpponentDraw.name, activeOpponentDraw.card)}</span>
            <Card card={activeOpponentDraw.card} faceUp size="small" disabled />
          </div>
        )}

        {drawnCard && (canDecide || animating) && (
          <div className={`game-board__drawn ${animating ? 'game-board__drawn--' + animating : ''}`}>
            <span>Drawn:</span>
            <div className={`game-board__drawn-card ${animating ? 'game-board__drawn-card--fly' : ''}`}>
              <Card card={drawnCard} faceUp size="small" />
            </div>
            <button className="btn btn--secondary btn--sm" onClick={handleDiscard} disabled={!!animating}>
              Discard
            </button>
          </div>
        )}
      </div>

      <div className="game-board__bottom">
        <div className="game-board__my-name">
          {dealerId === playerId && <span className="opponent__dealer-star">★ </span>}
          {players[playerId]?.displayName || 'You'}
        </div>
        {players[playerId]?.spectating ? (
          <div className="game-board__spectating">
            Watching this round — you'll be dealt in next round!
          </div>
        ) : (
          <PlayerHand
            hand={myHand}
            onCardClick={handleCardClick}
            gamePhase={gameState.phase}
            revealMode={isRevealing}
            revealedPositions={revealedPositions}
          />
        )}
      </div>

      {error && <div className="game-board__error">{getFriendlyError(error)}</div>}
    </div>
  );
}

export default GameBoard;
